import { createContext, useContext } from 'react';

/**
 * Carries a `Field`'s label and hint ids to the DS control inside it.
 *
 * `Input` and `Textarea` each render their own HeroUI TextField, which starts
 * a fresh label context — so a `Field` wrapped around one used to leave the
 * control nameless: screen readers announced an unlabeled box and clicking
 * the label did not focus it. The control reads these ids when it has no
 * label of its own.
 */
export const FieldLabelContext = createContext<{
  labelId?: string;
  descriptionId?: string;
} | null>(null);

/** aria props a nameless DS control inherits from an enclosing `Field`. */
export function useFieldLabelling(own: {
  label?: unknown;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}): { 'aria-labelledby'?: string; 'aria-describedby'?: string } {
  const field = useContext(FieldLabelContext);
  if (!field || own.label || own['aria-label'] || own['aria-labelledby'])
    return {};
  return {
    ...(field.labelId ? { 'aria-labelledby': field.labelId } : {}),
    ...(field.descriptionId && !own['aria-describedby']
      ? { 'aria-describedby': field.descriptionId }
      : {}),
  };
}
